import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { translations } from '@/data/translations';
import { useFeaturedProducts, useCategories } from '@/hooks/useSupabase';
import { ProductCard } from '@/components/ProductCard';
import { ProductCardSkeleton } from '@/components/Skeletons';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

// Featured product IDs from Supabase
const FEATURED_PRODUCT_IDS = [437, 645, 435, 499];

export default function HomePage() {
  const { t, getLocalizedField } = useLanguage();
  const { products: featuredProducts, loading: productsLoading } = useFeaturedProducts(FEATURED_PRODUCT_IDS);
  const { categories } = useCategories();

  // Get category by slug helper
  const getCat = (slug) => categories.find(c => c.slug === slug);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/web_home_cover.webp"
            alt="Artisan workshop atmosphere"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="max-w-3xl">
            <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-none mb-6">
              {t(translations.home.heroTitle)}
            </h1>
            <p className="font-sans text-lg md:text-xl text-white/80 leading-relaxed mb-8">
              {t(translations.home.heroSubtitle)}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="rounded-full bg-white text-[#2C2C2C] px-8 py-4 h-auto hover:bg-[#F9F8F6] transition-colors duration-300 text-sm uppercase tracking-widest"
              >
                <Link to="/catalog" data-testid="hero-catalog-btn">
                  {t(translations.home.viewCatalog)}
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="rounded-full border-white text-white px-8 py-4 h-auto hover:bg-white hover:text-[#2C2C2C] transition-colors duration-300 text-sm uppercase tracking-widest bg-transparent"
              >
                <Link to="/craft" data-testid="hero-craft-btn">
                  {t(translations.home.ourCraft)}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex justify-between items-end mb-12">
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] tracking-tight">
              {t(translations.home.featuredPieces)}
            </h2>
            <Link
              to="/catalog"
              className="hidden md:flex items-center font-sans text-sm uppercase tracking-widest text-[#2C2C2C] hover:text-[#8B5A2B] transition-colors"
              data-testid="view-all-link"
            >
              {t(translations.catalog.viewDetails)}
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>

          {productsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
              {[...Array(4)].map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          <div className="mt-12 md:hidden text-center">
            <Button asChild variant="outline" className="rounded-full border-[#2C2C2C] text-[#2C2C2C] px-8 py-4 h-auto">
              <Link to="/catalog">
                {t(translations.home.viewCatalog)}
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Categories Grid - Tetris Style */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-[1800px] mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] tracking-tight mb-12">
            {t(translations.nav.catalog)}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-8">
            {/* Large Feature - Tables */}
            <Link 
              to="/catalog?category=tables" 
              className="lg:col-span-8 lg:row-span-2 group relative aspect-[16/9] lg:aspect-auto overflow-hidden rounded-sm"
              data-testid="cat-tables"
            >
              <img 
                src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/web_home_tables_category.webp"
                alt={getLocalizedField(getCat('tables'), 'name') || 'Tables'}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute bottom-6 left-6">
                <span className="font-serif text-2xl md:text-3xl text-white">
                  {getLocalizedField(getCat('tables'), 'name') || 'Tables'}
                </span>
              </div>
            </Link>

            {/* Chairs */}
            <Link 
              to="/catalog?category=chairs" 
              className="lg:col-span-4 group relative aspect-square overflow-hidden rounded-sm"
              data-testid="cat-chairs"
            >
              <img 
                src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/web_home_chairs_category.webp"
                alt={getLocalizedField(getCat('chairs'), 'name') || 'Chairs'}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute bottom-6 left-6">
                <span className="font-serif text-xl md:text-2xl text-white">
                  {getLocalizedField(getCat('chairs'), 'name') || 'Chairs'}
                </span>
              </div>
            </Link>

            {/* Storage */}
            <Link 
              to="/catalog?category=storage" 
              className="lg:col-span-4 group relative aspect-square overflow-hidden rounded-sm"
              data-testid="cat-storage"
            >
              <img 
                src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/web_aufbewarung_category.webp"
                alt={getLocalizedField(getCat('storage'), 'name') || 'Storage'}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute bottom-6 left-6">
                <span className="font-serif text-xl md:text-2xl text-white">
                  {getLocalizedField(getCat('storage'), 'name') || 'Storage'}
                </span>
              </div>
            </Link>

            {/* ARS Sacra */}
            <Link 
              to="/catalog?category=ars-sacra" 
              className="lg:col-span-4 group relative aspect-[4/3] overflow-hidden rounded-sm"
              data-testid="cat-ars-sacra"
            >
              <img 
                src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/AS%20010.webp"
                alt={getLocalizedField(getCat('ars-sacra'), 'name') || 'ARS Sacra'}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute bottom-6 left-6">
                <span className="font-serif text-xl md:text-2xl text-white">
                  {getLocalizedField(getCat('ars-sacra'), 'name') || 'ARS Sacra'}
                </span>
              </div>
            </Link>

            {/* Paintings */}
            <Link 
              to="/catalog?category=paintings" 
              className="lg:col-span-4 group relative aspect-[4/3] overflow-hidden rounded-sm"
              data-testid="cat-paintings"
            >
              <img 
                src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/BI%20138.webp"
                alt={getLocalizedField(getCat('paintings'), 'name') || 'Paintings'}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute bottom-6 left-6">
                <span className="font-serif text-xl md:text-2xl text-white">
                  {getLocalizedField(getCat('paintings'), 'name') || 'Paintings'}
                </span>
              </div>
            </Link>

            {/* Other */}
            <Link 
              to="/catalog?category=other" 
              className="lg:col-span-4 group relative aspect-[4/3] overflow-hidden rounded-sm"
              data-testid="cat-other"
            >
              <img 
                src="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/AN%20122(2).webp"
                alt={getLocalizedField(getCat('other'), 'name') || 'Other'}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute bottom-6 left-6">
                <span className="font-serif text-xl md:text-2xl text-white">
                  {getLocalizedField(getCat('other'), 'name') || 'Other'}
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1800px] mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] tracking-tight mb-6">
            {t(translations.craft.title)}
          </h2>
          <p className="font-sans text-lg md:text-xl text-[#5D5D5D] max-w-2xl mx-auto mb-8">
            {t(translations.craft.subtitle)}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              asChild
              className="rounded-full bg-[#2C2C2C] text-white px-8 py-4 h-auto hover:bg-[#4A5D4F] transition-colors duration-300 text-sm uppercase tracking-widest"
            >
              <Link to="/craft" data-testid="cta-craft-btn">
                {t(translations.home.ourCraft)}
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full border-[#2C2C2C] text-[#2C2C2C] px-8 py-4 h-auto hover:bg-[#2C2C2C] hover:text-white transition-colors duration-300 text-sm uppercase tracking-widest"
            >
              <a
                href="https://cztvdlfmjrmliznubmzs.supabase.co/storage/v1/object/public/Product%20images/katalog.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-lookbook-btn"
              >
                {t(translations.home.lookbook)}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
